import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "./api/client";

const DEFAULT_TRADE_NOTIFICATIONS = Object.freeze({
  pending: [],
  accepted: [],
  summary: { pendingCount: 0, awaitingResponseCount: 0, awaitingCounterpartyCount: 0 }
});

const DEFAULT_MESSAGE_NOTIFICATIONS = Object.freeze({
  conversations: [],
  summary: { conversationCount: 0, unreadCount: 0 }
});

export function truncateNotificationPreview(value, maxLength = 88) {
  const normalizedValue = String(value || "").trim();

  if (normalizedValue.length <= maxLength) {
    return normalizedValue;
  }

  return `${normalizedValue.slice(0, maxLength - 1).trimEnd()}…`;
}

function toTimestamp(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const parsed = Date.parse(value || "");
  return Number.isFinite(parsed) ? parsed : 0;
}

function buildNotificationSeenKey(item) {
  return Number(item?.counterparty?.id) || 0;
}

function toIsoString(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? new Date(value).toISOString() : null;
  }

  const parsed = Date.parse(value || "");
  return Number.isFinite(parsed) ? new Date(parsed).toISOString() : null;
}

function normalizeNotificationStates(values) {
  const nextValue = new Map();

  (Array.isArray(values) ? values : []).forEach((entry) => {
    const counterpartyUserId = Number(entry?.counterpartyUserId) || 0;
    const lastSeenAt = toIsoString(entry?.lastSeenAt);

    if (counterpartyUserId > 0 && lastSeenAt) {
      nextValue.set(counterpartyUserId, lastSeenAt);
    }
  });

  return nextValue;
}

function formatCountLabel(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function joinNotificationSegments(segments) {
  const normalizedSegments = segments.filter(Boolean);

  if (normalizedSegments.length === 0) {
    return "";
  }

  if (normalizedSegments.length === 1) {
    return normalizedSegments[0];
  }

  if (normalizedSegments.length === 2) {
    return `${normalizedSegments[0]} and ${normalizedSegments[1]}`;
  }

  return `${normalizedSegments.slice(0, -1).join(", ")}, and ${normalizedSegments[normalizedSegments.length - 1]}`;
}

function isAcceptedByCounterparty(deal) {
  const acceptedAt = toTimestamp(deal?.acceptedAt || deal?.updatedAt);
  const currentUserStateUpdatedAt = toTimestamp(deal?.currentUserStateUpdatedAt);
  const counterpartyStateUpdatedAt = toTimestamp(deal?.counterpartyStateUpdatedAt);

  if (acceptedAt <= 0 || counterpartyStateUpdatedAt <= 0) {
    return false;
  }

  return counterpartyStateUpdatedAt >= acceptedAt && counterpartyStateUpdatedAt > currentUserStateUpdatedAt;
}

function buildNotificationItems(tradeNotifications, messageNotifications) {
  const itemsByCounterpartyId = new Map();
  const unreadConversations = Array.isArray(messageNotifications?.conversations) ? messageNotifications.conversations : [];
  const actionableTrades = (Array.isArray(tradeNotifications?.pending) ? tradeNotifications.pending : [])
    .filter((deal) => Boolean(deal?.awaitingResponse));
  const acceptedTrades = (Array.isArray(tradeNotifications?.accepted) ? tradeNotifications.accepted : [])
    .filter((deal) => isAcceptedByCounterparty(deal));

  const ensureItem = (counterparty) => {
    const counterpartyId = Number(counterparty?.id);

    if (!Number.isFinite(counterpartyId) || counterpartyId <= 0) {
      return null;
    }

    if (!itemsByCounterpartyId.has(counterpartyId)) {
      itemsByCounterpartyId.set(counterpartyId, {
        counterparty: {
          id: counterpartyId,
          username: counterparty?.username || `User ${counterpartyId}`,
          collectorTier: counterparty?.collectorTier || null,
          role: counterparty?.role || null
        },
        unreadCount: 0,
        latestMessage: null,
        messagePath: `/account/messages?userId=${encodeURIComponent(String(counterpartyId))}`,
        pendingTrades: [],
        acceptedTrades: [],
        tradePath: null,
        lastActivityAt: 0
      });
    }

    return itemsByCounterpartyId.get(counterpartyId);
  };

  unreadConversations.forEach((conversation) => {
    const item = ensureItem(conversation?.counterparty);

    if (!item) {
      return;
    }

    item.unreadCount = Number(conversation?.unreadCount) || 0;
    item.latestMessage = conversation?.latestMessage || null;
    item.lastActivityAt = Math.max(
      item.lastActivityAt,
      toTimestamp(conversation?.latestMessage?.createdAt || conversation?.lastActivityAt)
    );
  });

  actionableTrades.forEach((deal) => {
    const item = ensureItem(deal?.counterparty);

    if (!item) {
      return;
    }

    item.pendingTrades.push(deal);
    item.tradePath = item.tradePath || `/tradefinder?pendingTradeId=${encodeURIComponent(String(deal.id))}`;
    item.lastActivityAt = Math.max(item.lastActivityAt, toTimestamp(deal?.updatedAt || deal?.createdAt));
  });

  acceptedTrades.forEach((deal) => {
    const item = ensureItem(deal?.counterparty);

    if (!item) {
      return;
    }

    item.acceptedTrades.push(deal);
    item.tradePath = item.tradePath || `/tradefinder?acceptedTradeId=${encodeURIComponent(String(deal.id))}`;
    item.lastActivityAt = Math.max(item.lastActivityAt, toTimestamp(deal?.acceptedAt || deal?.updatedAt || deal?.createdAt));
  });

  return Array.from(itemsByCounterpartyId.values())
    .map((item) => {
      const pendingTradeCount = item.pendingTrades.length;
      const acceptedTradeCount = item.acceptedTrades.length;
      const tradeCount = pendingTradeCount + acceptedTradeCount;
      const hasUnreadMessage = item.unreadCount > 0;
      const hasTradeProposal = pendingTradeCount > 0;
      const hasAcceptedTrade = acceptedTradeCount > 0;

      const headline = joinNotificationSegments([
        hasUnreadMessage ? formatCountLabel(item.unreadCount, "new message") : "",
        hasTradeProposal ? formatCountLabel(pendingTradeCount, "trade proposal") : "",
        hasAcceptedTrade ? formatCountLabel(acceptedTradeCount, "accepted trade") : ""
      ]);

      const notificationText = `${joinNotificationSegments([
        hasAcceptedTrade ? (acceptedTradeCount > 1 ? "accepted your trades" : "accepted your trade") : "",
        hasTradeProposal ? (pendingTradeCount > 1 ? "sent you trade proposals" : "sent you a trade proposal") : "",
        hasUnreadMessage ? (item.unreadCount > 1 ? "sent you new messages" : "sent you a new message") : ""
      ]).replace(/^./, (character) => character.toUpperCase())}.`;

      return {
        ...item,
        pendingTrades: item.pendingTrades.sort((left, right) => toTimestamp(right?.updatedAt || right?.createdAt) - toTimestamp(left?.updatedAt || left?.createdAt)),
        acceptedTrades: item.acceptedTrades.sort((left, right) => toTimestamp(right?.acceptedAt || right?.updatedAt || right?.createdAt) - toTimestamp(left?.acceptedAt || left?.updatedAt || left?.createdAt)),
        tradeCount,
        pendingTradeCount,
        acceptedTradeCount,
        hasUnreadMessage,
        hasTradeProposal,
        hasAcceptedTrade,
        headline,
        browsePath: `/browse/users/${encodeURIComponent(String(item.counterparty.username || item.counterparty.id))}`,
        actionLabel: hasTradeProposal || hasAcceptedTrade ? "Trade" : "Messages",
        primaryPath: hasTradeProposal || hasAcceptedTrade ? (item.tradePath || "/tradefinder") : item.messagePath,
        notificationText,
        preview: hasUnreadMessage
          ? truncateNotificationPreview(item.latestMessage?.body || "New unread message")
          : hasAcceptedTrade
            ? (acceptedTradeCount === 1 ? "Your trade was accepted and is ready in TradeFinder." : `${acceptedTradeCount} accepted trades are ready in TradeFinder.`)
            : (pendingTradeCount === 1 ? "A trade proposal is waiting for your response." : `${pendingTradeCount} trade proposals are waiting for your response.`)
      };
    })
    .sort((left, right) => right.lastActivityAt - left.lastActivityAt);
}

export function useNotifications({ isAuthenticated, selectedUserId }) {
  const [tradeNotifications, setTradeNotifications] = useState(DEFAULT_TRADE_NOTIFICATIONS);
  const [messageNotifications, setMessageNotifications] = useState(DEFAULT_MESSAGE_NOTIFICATIONS);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationStates, setNotificationStates] = useState(() => new Map());

  useEffect(() => {
    if (!isAuthenticated || !selectedUserId) {
      setTradeNotifications(DEFAULT_TRADE_NOTIFICATIONS);
      setMessageNotifications(DEFAULT_MESSAGE_NOTIFICATIONS);
      setNotificationsLoading(false);
      setNotificationStates(new Map());
      return undefined;
    }

    let isDisposed = false;

    const loadNotifications = async () => {
      try {
        setNotificationsLoading(true);
        const [tradeResponse, messagesResponse] = await Promise.all([
          apiFetch("/api/trades/active", { userId: selectedUserId }),
          apiFetch("/api/notifications", { userId: selectedUserId })
        ]);
        const tradeData = await tradeResponse.json().catch(() => ({}));
        const messagesData = await messagesResponse.json().catch(() => ({}));

        if (!tradeResponse.ok) {
          throw new Error(tradeData.error || "Could not load trade notifications");
        }

        if (!messagesResponse.ok) {
          throw new Error(messagesData.error || "Could not load message notifications");
        }

        if (isDisposed) {
          return;
        }

        setTradeNotifications({
          pending: Array.isArray(tradeData.pending) ? tradeData.pending : [],
          accepted: Array.isArray(tradeData.accepted) ? tradeData.accepted : [],
          summary: tradeData.summary || DEFAULT_TRADE_NOTIFICATIONS.summary
        });
        setMessageNotifications({
          conversations: (Array.isArray(messagesData.conversations) ? messagesData.conversations : [])
            .filter((conversation) => Number(conversation?.unreadCount) > 0),
          summary: messagesData.summary || DEFAULT_MESSAGE_NOTIFICATIONS.summary
        });
        setNotificationStates(normalizeNotificationStates(messagesData.notificationStates));
      } catch (error) {
        if (!isDisposed) {
          console.error("Notifications load error:", error);
          setTradeNotifications(DEFAULT_TRADE_NOTIFICATIONS);
          setMessageNotifications(DEFAULT_MESSAGE_NOTIFICATIONS);
          setNotificationStates(new Map());
        }
      } finally {
        if (!isDisposed) {
          setNotificationsLoading(false);
        }
      }
    };

    loadNotifications();

    return () => {
      isDisposed = true;
    };
  }, [isAuthenticated, selectedUserId]);

  const markNotificationSeen = useCallback((notificationItem) => {
    const counterpartyUserId = buildNotificationSeenKey(notificationItem);
    const lastSeenAt = toIsoString(notificationItem?.lastActivityAt);

    if (!counterpartyUserId || !lastSeenAt) {
      return Promise.resolve();
    }

    setNotificationStates((currentValue) => {
      const existingValue = currentValue.get(counterpartyUserId);

      if (existingValue && Date.parse(existingValue) >= Date.parse(lastSeenAt)) {
        return currentValue;
      }

      const nextValue = new Map(currentValue);
      nextValue.set(counterpartyUserId, lastSeenAt);
      return nextValue;
    });

    return apiFetch(`/api/notifications/${encodeURIComponent(String(counterpartyUserId))}/read`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ lastSeenAt })
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(payload.error || "Could not update notification state");
        }

        const persistedLastSeenAt = toIsoString(payload?.notificationState?.lastSeenAt) || lastSeenAt;
        setNotificationStates((currentValue) => {
          const nextValue = new Map(currentValue);
          nextValue.set(counterpartyUserId, persistedLastSeenAt);
          return nextValue;
        });
      })
      .catch((error) => {
        console.error("Notification read-state update error:", error);
      });
  }, []);

  return useMemo(() => {
    const notificationItems = buildNotificationItems(tradeNotifications, messageNotifications);
    const decoratedNotificationItems = notificationItems.map((item) => ({
      ...item,
      isUnread: (() => {
        const counterpartyUserId = buildNotificationSeenKey(item);
        const lastSeenAt = notificationStates.get(counterpartyUserId);

        if (!lastSeenAt) {
          return true;
        }

        return toTimestamp(item?.lastActivityAt) > toTimestamp(lastSeenAt);
      })()
    }));
    const unreadNotificationItems = decoratedNotificationItems.filter((item) => item.isUnread);
    const unreadNotificationsCount = unreadNotificationItems.length;

    return {
      tradeNotifications,
      messageNotifications,
      notificationItems: decoratedNotificationItems,
      unreadNotificationItems,
      notificationsLoading,
      hasNotifications: decoratedNotificationItems.length > 0,
      hasUnreadNotifications: unreadNotificationsCount > 0,
      notificationsCount: unreadNotificationsCount,
      markNotificationSeen
    };
  }, [markNotificationSeen, messageNotifications, notificationStates, notificationsLoading, tradeNotifications]);
}